/**
 * Compare a canvas pixel-by-pixel against a half-size reference image.
 * The rendered canvas is downscaled to match the reference before comparison.
 *
 * @param {HTMLCanvasElement} canvasA - The rendered canvas (full size)
 * @param {HTMLImageElement} refImage - The reference bitmap (half size)
 * @param {Object} options
 * @param {number} options.pixelTolerance - Max RGBA distance to consider same (default 5)
 * @param {number} options.failThreshold - Max % of differing pixels before fail (default 0.1)
 * @returns {{ pass: boolean, diffPercent: number, diffCanvas: HTMLCanvasElement }}
 */
export function compareCanvasToReference(canvasA, refImage, options) {
    var pixelTolerance = (options && options.pixelTolerance) || 5;
    var failThreshold = (options && options.failThreshold) || 3.5;

    var halfWidth = Math.round(canvasA.width / 2);
    var halfHeight = Math.round(canvasA.height / 2);

    var scaledCanvas = document.createElement("canvas");
    scaledCanvas.width = halfWidth;
    scaledCanvas.height = halfHeight;
    var scaledCtx = scaledCanvas.getContext("2d");
    scaledCtx.drawImage(canvasA, 0, 0, halfWidth, halfHeight);

    var refCanvas = document.createElement("canvas");
    refCanvas.width = halfWidth;
    refCanvas.height = halfHeight;
    var refCtx = refCanvas.getContext("2d");
    refCtx.drawImage(refImage, 0, 0, halfWidth, halfHeight);

    var dataA = scaledCtx.getImageData(0, 0, halfWidth, halfHeight).data;
    var dataRef = refCtx.getImageData(0, 0, halfWidth, halfHeight).data;

    var diffCanvas = document.createElement("canvas");
    diffCanvas.width = halfWidth;
    diffCanvas.height = halfHeight;
    var diffCtx = diffCanvas.getContext("2d");
    var diffImageData = diffCtx.createImageData(halfWidth, halfHeight);
    var diffData = diffImageData.data;

    var totalPixels = halfWidth * halfHeight;
    var diffPixels = 0;

    for (var i = 0; i < dataA.length; i += 4) {
        var dr = dataA[i] - dataRef[i];
        var dg = dataA[i + 1] - dataRef[i + 1];
        var db = dataA[i + 2] - dataRef[i + 2];
        var da = dataA[i + 3] - dataRef[i + 3];
        var dist = Math.sqrt(dr * dr + dg * dg + db * db + da * da);

        if (dist > pixelTolerance) {
            diffPixels++;
            diffData[i] = 255;
            diffData[i + 1] = 0;
            diffData[i + 2] = 0;
            diffData[i + 3] = 255;
        } else {
            diffData[i] = dataRef[i];
            diffData[i + 1] = dataRef[i + 1];
            diffData[i + 2] = dataRef[i + 2];
            diffData[i + 3] = Math.floor(dataRef[i + 3] * 0.3);
        }
    }

    diffCtx.putImageData(diffImageData, 0, 0);

    var diffPercent = (diffPixels / totalPixels) * 100;

    return {
        pass: diffPercent <= failThreshold,
        diffPercent: Math.round(diffPercent * 1000) / 1000,
        diffCanvas: diffCanvas
    };
}
